import 'react';
import {Box, MenuItem, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {A1_CONTENT} from '@/pages/components/audit-program/a-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {yellowFieldSx} from '@/pages/components/audit-program/a-section/yellowFieldSx';
import ProgramDateField from '@/pages/components/audit-program/ProgramDateField';

export interface A1MemorandumViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    /** Template copy; defaults to code constant when omitted. */
    content?: typeof A1_CONTENT;
}

const YES_NO = ['', 'YES', 'NO'];

function YellowInput({
    value,
    onChange,
    multiline,
    minRows,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    minRows?: number;
    placeholder?: string;
}) {
    return (
        <Box sx={yellowFieldSx}>
            <InputField
                type="text"
                fullWidth
                compact
                align="left"
                multiline={multiline}
                minRows={minRows}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </Box>
    );
}

function YesNoSelect({value, onChange}: {value?: string; onChange: (v: string) => void}) {
    return (
        <InputField
            select
            compact
            align="left"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{minWidth: 100}}
        >
            {YES_NO.map((opt) => (
                <MenuItem key={opt || 'blank'} value={opt}>{opt || '—'}</MenuItem>
            ))}
        </InputField>
    );
}

/**
 * Layout mirrors Excel sheet A1 (Final Completion Memorandum):
 * - Header: Client / Year end / File no | Ref A1
 * - Title + intro
 * - §1 confirm list + partner / EQR signature block (name left, date right)
 * - §2 B1 matters
 * - §3 Subsequent Events + A1.2 YES/NO
 * - §4 Other + A1.3 YES/NO
 * - Footer HKSA note
 */
export default function A1MemorandumView({header, values, onChange, content}: A1MemorandumViewProps) {
    const copy = content ?? A1_CONTENT;
    const section3Text = values.section3Text ?? copy.section3DefaultText;
    const section4Text = values.section4Text ?? 'N/A';

    return (
        <Box sx={{p: 2, maxWidth: 960}}>
            <ProgramSectionHeader header={header} variant="a1" refCode="A1" values={values} onChange={onChange}/>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.5, mb: 1.5, mt: 1}}
            >
                {copy.title}
            </Typography>

            <Typography variant="body2" sx={{mb: 2, lineHeight: 1.5}}>
                {copy.intro}
            </Typography>

            {/* 1. Approval */}
            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.section1Title}
            </Typography>
            <Typography variant="body2" sx={{mb: 1}}>
                {copy.confirmIntro}
            </Typography>
            <Stack spacing={0.75} sx={{mb: 2, pl: 0.5}}>
                {copy.confirmItems.map((item) => (
                    <Stack key={item.num} direction="row" spacing={1.5} alignItems="flex-start">
                        <Typography variant="body2" sx={{minWidth: 18, fontWeight: 600}}>{item.num}</Typography>
                        <Typography variant="body2" sx={{lineHeight: 1.45}}>{item.text}</Typography>
                    </Stack>
                ))}
            </Stack>

            <Typography variant="body2" sx={{mb: 2}}>
                {copy.authorizeText}
            </Typography>

            {/* Partner signature: name left (A15), date right (J15) — labels under */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 160px'},
                    columnGap: 4,
                    rowGap: 0.5,
                    mb: 3,
                    alignItems: 'start',
                }}
            >
                <Box>
                    <YellowInput
                        value={values.partnerName ?? header.partnerName ?? ''}
                        onChange={(v) => onChange('partnerName', v)}
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{display: 'block', mt: 0.25, lineHeight: 1.25}}
                    >
                        {copy.partnerLabel}
                    </Typography>
                </Box>
                <ProgramDateField
                    value={values.partnerDate ?? ''}
                    onChange={(v) => onChange('partnerDate', v)}
                />
            </Box>

            {/* EQR: label left, date right (J18/J19) */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 160px'},
                    columnGap: 4,
                    rowGap: 0.5,
                    mb: 3,
                    alignItems: 'start',
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{display: 'block', pt: {sm: 1}, lineHeight: 1.25}}
                >
                    {copy.eqrLabel}
                </Typography>
                <ProgramDateField
                    value={values.eqrDate ?? ''}
                    onChange={(v) => onChange('eqrDate', v)}
                />
            </Box>

            {/* 2. Matters outstanding on B1 */}
            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.section2Title}
            </Typography>
            <Box sx={{mb: 3}}>
                <YellowInput
                    value={values.b1Matters ?? ''}
                    onChange={(v) => onChange('b1Matters', v)}
                    multiline
                    minRows={2}
                    placeholder="N/A"
                />
            </Box>

            {/* 3. Final Subsequent Events Review | A1.2 Completed YES/NO */}
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                justifyContent="space-between"
                alignItems={{xs: 'flex-start', sm: 'center'}}
                spacing={1}
                sx={{mb: 1}}
            >
                <Typography variant="body2" fontWeight={700}>
                    {copy.section3Title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                        A1.2 Completed -
                    </Typography>
                    <YesNoSelect
                        value={values.a12Completed}
                        onChange={(v) => onChange('a12Completed', v)}
                    />
                </Stack>
            </Stack>
            <Box sx={{mb: 3}}>
                <YellowInput
                    value={section3Text}
                    onChange={(v) => onChange('section3Text', v)}
                    multiline
                    minRows={3}
                />
            </Box>

            {/* 4. Other | A1.3 Completed YES/NO */}
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                justifyContent="space-between"
                alignItems={{xs: 'flex-start', sm: 'center'}}
                spacing={1}
                sx={{mb: 1}}
            >
                <Typography variant="body2" fontWeight={700}>
                    {copy.section4Title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                        A1.3 Completed -
                    </Typography>
                    <YesNoSelect
                        value={values.a13Completed}
                        onChange={(v) => onChange('a13Completed', v)}
                    />
                </Stack>
            </Stack>
            <Box sx={{mb: 3}}>
                <YellowInput
                    value={section4Text}
                    onChange={(v) => onChange('section4Text', v)}
                    multiline
                    minRows={2}
                    placeholder="N/A"
                />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{display: 'block', whiteSpace: 'pre-line', lineHeight: 1.5}}>
                {copy.footerNote}
            </Typography>
        </Box>
    );
}
